import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { Building2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function ListBusiness() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    industry: "",
    location: "",
    asking_price: "",
    revenue: "",
    profit: "",
    description: "",
    assets: "",
    liabilities: "",
    images: [],
    documents: []
  });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [businessImages, setBusinessImages] = useState([]);
  const [documents, setDocuments] = useState([]);

  const industries = ["Technology", "Retail", "Manufacturing", "Healthcare", "Food & Beverage", "Real Estate", "Services", "Other"];

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return data.url;
      });

      const urls = await Promise.all(uploadPromises);
      setBusinessImages([...businessImages, ...urls]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDocumentUpload = async (e, docType) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return {
          name: file.name,
          url: data.url,
          type: docType,
          size: data.size
        };
      });

      const uploadedDocs = await Promise.all(uploadPromises);
      setDocuments([...documents, ...uploadedDocs]);
      toast.success(`${files.length} document(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload documents');
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeImage = (index) => {
    setBusinessImages(businessImages.filter((_, i) => i !== index));
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        ...form,
        asking_price: parseFloat(form.asking_price),
        revenue: parseFloat(form.revenue),
        profit: parseFloat(form.profit),
        images: businessImages,
        documents: documents
      };
      
      await api.post("/listings", payload);
      toast.success("Business listed successfully! Pending admin approval.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to list business");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <Card data-testid="list-business-form">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-8 h-8 text-blue-600" />
              <CardTitle className="text-3xl">List Your Business</CardTitle>
            </div>
            <CardDescription>Fill in the details to list your business for sale</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Business Name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="business-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="industry">Industry *</Label>
                  <Select
                    value={form.industry}
                    onValueChange={(value) => setForm({ ...form, industry: value })}
                    required
                  >
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  placeholder="City, State"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  required
                  data-testid="location-input"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="asking_price">Asking Price (₹) *</Label>
                  <Input
                    id="asking_price"
                    type="number"
                    value={form.asking_price}
                    onChange={(e) => setForm({ ...form, asking_price: e.target.value })}
                    required
                    data-testid="asking-price-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="revenue">Annual Revenue (₹) *</Label>
                  <Input
                    id="revenue"
                    type="number"
                    value={form.revenue}
                    onChange={(e) => setForm({ ...form, revenue: e.target.value })}
                    required
                    data-testid="revenue-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profit">Annual Profit (₹) *</Label>
                  <Input
                    id="profit"
                    type="number"
                    value={form.profit}
                    onChange={(e) => setForm({ ...form, profit: e.target.value })}
                    required
                    data-testid="profit-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  rows={5}
                  placeholder="Describe your business, its history, operations, and why you're selling..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  required
                  data-testid="description-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="assets">Assets</Label>
                  <Textarea
                    id="assets"
                    rows={3}
                    placeholder="List key assets (equipment, inventory, real estate, etc.)"
                    value={form.assets}
                    onChange={(e) => setForm({ ...form, assets: e.target.value })}
                    data-testid="assets-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="liabilities">Liabilities</Label>
                  <Textarea
                    id="liabilities"
                    rows={3}
                    placeholder="List any liabilities (debts, loans, etc.)"
                    value={form.liabilities}
                    onChange={(e) => setForm({ ...form, liabilities: e.target.value })}
                    data-testid="liabilities-input"
                  />
                </div>
              </div>

              {/* Attachments Section */}
              <div className="space-y-6 pt-6 border-t border-slate-200">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Business Images</h3>
                  <p className="text-sm text-slate-600 mb-4">Upload photos of your business premises, products, or operations</p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="business-images" className="cursor-pointer">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors">
                          <Input
                            id="business-images"
                            type="file"
                            accept="image/jpeg,image/jpg,image/png"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                            data-testid="business-images-input"
                          />
                          <div className="flex flex-col items-center gap-2">
                            <Building2 className="w-8 h-8 text-slate-400" />
                            <p className="text-sm font-medium text-slate-700">Click to upload images</p>
                            <p className="text-xs text-slate-500">JPG, PNG (Max 10MB per file)</p>
                          </div>
                        </div>
                      </Label>
                    </div>

                    {businessImages.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {businessImages.map((url, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={url}
                              alt={`Business ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`remove-image-${index}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Business Documents</h3>
                  <p className="text-sm text-slate-600 mb-4">Upload financial statements, business plans, and other supporting documents</p>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Financial Statements */}
                    <div className="space-y-2">
                      <Label htmlFor="financial-docs">Financial Statements (P&L, Balance Sheet)</Label>
                      <Input
                        id="financial-docs"
                        type="file"
                        accept=".pdf,.xlsx,.xls"
                        multiple
                        onChange={(e) => handleDocumentUpload(e, 'Financial Statement')}
                        disabled={uploadingFiles}
                        data-testid="financial-docs-input"
                      />
                    </div>

                    {/* Business Model */}
                    <div className="space-y-2">
                      <Label htmlFor="business-model">Business Model / Plan</Label>
                      <Input
                        id="business-model"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        multiple
                        onChange={(e) => handleDocumentUpload(e, 'Business Model')}
                        disabled={uploadingFiles}
                        data-testid="business-model-input"
                      />
                    </div>

                    {/* Business Review */}
                    <div className="space-y-2">
                      <Label htmlFor="business-review">Business Review / Audit Reports</Label>
                      <Input
                        id="business-review"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        multiple
                        onChange={(e) => handleDocumentUpload(e, 'Business Review')}
                        disabled={uploadingFiles}
                        data-testid="business-review-input"
                      />
                    </div>

                    {/* Other Documents */}
                    <div className="space-y-2">
                      <Label htmlFor="other-docs">Other Supporting Documents</Label>
                      <Input
                        id="other-docs"
                        type="file"
                        accept=".pdf,.doc,.docx,.xlsx,.xls"
                        multiple
                        onChange={(e) => handleDocumentUpload(e, 'Other')}
                        disabled={uploadingFiles}
                        data-testid="other-docs-input"
                      />
                    </div>
                  </div>

                  {/* Uploaded Documents List */}
                  {documents.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="text-sm font-medium text-slate-700">Uploaded Documents:</h4>
                      <div className="space-y-2">
                        {documents.map((doc, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                            data-testid={`document-${index}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="bg-blue-100 p-2 rounded">
                                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                                <p className="text-xs text-slate-500">{doc.type}</p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDocument(index)}
                              className="text-red-500 hover:text-red-700 p-1"
                              data-testid={`remove-document-${index}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {uploadingFiles && (
                    <p className="text-sm text-blue-600 mt-2">Uploading files...</p>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 w-full md:w-auto px-8 h-12"
                  disabled={loading}
                  data-testid="submit-listing-button"
                >
                  {loading ? "Submitting..." : "Submit for Approval"}
                </Button>
                <p className="text-sm text-slate-500 mt-3">Your listing will be reviewed by our team before going live.</p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}